import { useState } from 'react';
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Search,
  BedDouble,
  UtensilsCrossed,
  FileText,
  Loader2,
  Plus,
  Trash2,
  Receipt,
  AlertCircle,
} from 'lucide-react';
import { useBookings, useBooking } from '@/hooks/api/useBookings';
import type { BookingsQuery } from '@/services/api/bookings.api';
import { SourceBadge } from '@/components/reservations/SourceBadge';
import { useFolioSummary, useAddFolioItem, useRemoveFolioItem, usePrintBill } from '@/hooks/api/useFolio';
import { showToast } from '@/lib/toast';
import { extractErrorMessage } from '@/lib/error';
import { useChargeTypes } from '@/hooks/api/useChargeTypes';
import { usePropertyContext, useSelectedPropertyId } from '@/contexts/PropertyContext';
import { BillPreviewModal } from '@/components/ui/BillPreviewModal';
import { PaymentTypeBadge } from '@/components/ui/PaymentTypeBadge';
import { cn } from '@/lib/utils';
import type { Booking } from '@/services/api/bookings.api';

// ─── Constants ────────────────────────────────────────────────────────────────

// "All" excludes cancelled / no-show — billing context only cares about active bookings
const STATUS_FILTERS = [
  { value: 'active', label: 'All Active' },
  { value: 'checked_in', label: 'In-House' },
  { value: 'checked_out', label: 'Checked Out' },
  { value: 'confirmed', label: 'Confirmed' },
] as const;

const STATUS_BADGE: Record<string, string> = {
  checked_in: 'bg-green-100 text-green-700',
  checked_out: 'bg-gray-100 text-gray-600',
  confirmed: 'bg-blue-100 text-blue-700',
  pending: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-600',
  no_show: 'bg-red-100 text-red-600',
};

const STATUS_LABEL: Record<string, string> = {
  checked_in: 'In-House',
  checked_out: 'Checked Out',
  confirmed: 'Confirmed',
  pending: 'Pending',
  cancelled: 'Cancelled',
  no_show: 'No Show',
};

const FOOD_CATEGORIES = [
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'room_service', label: 'Room Service' },
  { value: 'food_beverage', label: 'Food & Beverage (Other)' },
  { value: 'minibar', label: 'Minibar' },
] as const;

const FOOD_CAT_SET = new Set(['food_beverage', 'breakfast', 'lunch', 'dinner', 'room_service', 'minibar']);
const ROOM_CAT_SET = new Set(['room_charge', 'extra_bed', 'late_checkout', 'parking', 'damage', 'other']);

function formatCategoryLabel(cat: string): string {
  const map: Record<string, string> = {
    room_charge: 'Room Charge',
    extra_bed: 'Extra Bed',
    late_checkout: 'Late Checkout',
    parking: 'Parking',
    damage: 'Damage',
    other: 'Other',
    tax: 'Tax',
    food_beverage: 'Food & Beverage',
    breakfast: 'Breakfast',
    lunch: 'Lunch',
    dinner: 'Dinner',
    room_service: 'Room Service',
    minibar: 'Minibar',
  };
  return map[cat] ?? cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatDate(d: string) {
  // Parse YYYY-MM-DD without timezone conversion — `new Date("2026-05-22")` is UTC
  // midnight which shows as the previous day in UTC-N timezones.
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y!, m! - 1, day!).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function calculateNights(checkIn: string, checkOut: string) {
  const diff = new Date(checkOut).getTime() - new Date(checkIn).getTime();
  return Math.max(1, Math.round(diff / 86_400_000));
}

function asRec(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;
}

/**
 * The per-night OTA-charged rate from the booking payload (`dailyPrice`) — what
 * the guest was actually charged, NOT the PMS rack rate or the sync snapshot.
 * Returns a formatted "OTA rate · $138/night × 3" sub-line, or undefined for
 * direct bookings / when the payload carries no numeric rate.
 */
function otaNightlySubLine(booking: Booking, nights: number): string | undefined {
  if (booking.source !== 'axis_rooms') return undefined;
  const payload = asRec(booking.channelPayload);
  if (!payload) return undefined;
  const rates = asRec(payload.Rates);
  const roomTypeArr = Array.isArray(rates?.roomType) ? (rates!.roomType as unknown[]) : [];
  const room = asRec(roomTypeArr[0]) ?? {};
  const flat = asRec(payload.booking) ?? payload;
  const bd = asRec(payload.BookingDetails) ?? {};
  const rawDaily = room.dailyPrice ?? room.dailyRate ?? flat.dailyPrice;
  if (rawDaily == null || rawDaily === '') return undefined;
  const daily = typeof rawDaily === 'number' ? rawDaily : parseFloat(String(rawDaily).replace(/,/g, ''));
  if (!Number.isFinite(daily)) return undefined;
  const ota = bd.ota ?? bd.bookingSource ?? flat.ota ?? 'OTA';
  return `${ota} rate · ${booking.currency} ${daily.toFixed(2)}/night × ${nights}`;
}

// ─── BookingList ──────────────────────────────────────────────────────────────

function BookingList({
  bookings,
  selectedId,
  onSelect,
  isLoading,
  search,
  onSearch,
}: {
  bookings: Booking[];
  selectedId: string | null;
  onSelect: (b: Booking) => void;
  isLoading: boolean;
  search: string;
  onSearch: (v: string) => void;
}) {
  const filtered = bookings.filter(b => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      b.guest?.fullName?.toLowerCase().includes(q) ||
      b.unit?.name?.toLowerCase().includes(q) ||
      b.id.toLowerCase().includes(q)
    );
  });

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-gray-100">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={e => onSearch(e.target.value)}
            placeholder="Search guest, room…"
            className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={20} className="animate-spin text-gray-400" />
          </div>
        )}
        {!isLoading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <Receipt size={32} className="text-gray-200 mb-2" aria-hidden="true" />
            <p className="text-sm text-gray-500">No bookings found</p>
          </div>
        )}
        {!isLoading && filtered.map(b => (
          <button
            key={b.id}
            type="button"
            onClick={() => onSelect(b)}
            className={cn(
              'w-full text-left px-4 py-3 border-b border-gray-50 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400',
              selectedId === b.id ? 'bg-primary-50 border-l-2 border-l-primary-500' : '',
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-gray-900 truncate">
                {b.guest?.fullName ?? 'Guest'}
              </p>
              <span className={cn(
                'shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full',
                STATUS_BADGE[b.status] ?? 'bg-gray-100 text-gray-600',
              )}>
                {STATUS_LABEL[b.status] ?? b.status}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{b.unit?.name ?? '—'}</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {formatDate(b.checkIn)} → {formatDate(b.checkOut)}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── MasterBillTab ────────────────────────────────────────────────────────────

// Consolidated view of the WHOLE folio: room + food + extras + taxes in one
// place, with a grand total / paid / balance summary. Line items are grouped
// into sections; the payment summary uses the backend-authoritative folio
// figures (totalCharges / totalPayments / balance) so the grand total never
// double-counts the room charge against the itemised lines below.
function MasterBillTab({ booking }: { booking: Booking }) {
  const { data: folio, isLoading, isError } = useFolioSummary(booking.id);
  // Enriched booking (channelPayload) — shared cache with the header + Room tab.
  const { data: enriched } = useBooking(booking.id);
  const nights = calculateNights(booking.checkIn, booking.checkOut);
  const otaSub = otaNightlySubLine(enriched ?? booking, nights);

  // Add-tax control — the hotel posts a tax/fee line at whatever amount + label
  // it needs (GST, VAT, city tax, service charge, …). No auto-percentage: the
  // property decides. Posts a `tax`-category folio line so it lands under Taxes.
  const addTax = useAddFolioItem();
  const isCheckedOut = booking.status === 'checked_out';
  const taxDate = (() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return today < booking.checkOut ? today : booking.checkOut;
  })();
  const [taxForm, setTaxForm] = useState({ label: '', amount: '' });
  const [taxErr, setTaxErr] = useState('');

  function handleAddTax() {
    setTaxErr('');
    const amt = parseFloat(taxForm.amount);
    if (!taxForm.label.trim()) { setTaxErr('Enter a label (e.g. GST 18%)'); return; }
    if (isNaN(amt) || amt <= 0) { setTaxErr('Enter a valid amount > 0'); return; }
    addTax.mutate(
      {
        bookingId: booking.id,
        payload: { category: 'tax', description: taxForm.label.trim(), amount: amt, date: taxDate },
      },
      {
        onSuccess: () => {
          setTaxForm({ label: '', amount: '' });
          showToast('Tax added to the bill');
        },
        onError: (e) => setTaxErr(extractErrorMessage(e)),
      },
    );
  }

  const items = folio?.items ?? [];
  const roomItems = items.filter(i => ROOM_CAT_SET.has(i.category));
  const foodItems = items.filter(i => FOOD_CAT_SET.has(i.category));
  const taxItems = items.filter(i => i.category === 'tax');
  const otherItems = items.filter(
    i => !ROOM_CAT_SET.has(i.category) && !FOOD_CAT_SET.has(i.category) && i.category !== 'tax',
  );

  // Room charge is booking.baseAmount UNLESS a room_charge folio item already
  // captures it — in that case the item is the source of truth and we must NOT
  // also render the base line, or the room charge would be counted twice.
  const base = Number(booking.baseAmount) || 0;
  const hasRoomChargeItem = roomItems.some(i => i.category === 'room_charge');
  // Fallback tax when the folio carries no explicit tax lines: derived from the
  // booking total the same way RoomBillTab does.
  const bookingTax = (Number(booking.totalAmount) || 0) - base;

  const hasCurrencyMismatch = items.some(i => i.currency && i.currency !== booking.currency);

  // Auto per-section tax computed by the backend from the property's room/food
  // tax %. When set, we show the computed Room/Food tax (and it's already rolled
  // into folio.totalCharges) instead of the manual/derived fallback.
  const taxBreakdown = folio?.tax;
  const hasAutoTax =
    !!taxBreakdown && (taxBreakdown.room.taxAmount > 0 || taxBreakdown.food.taxAmount > 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={20} className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError || !folio) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertCircle size={28} className="text-red-300 mb-2" aria-hidden="true" />
        <p className="text-sm text-gray-500">Couldn't load this bill. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stay summary */}
      <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide">Check-in</p>
          <p className="font-medium text-gray-800">{formatDate(booking.checkIn)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide">Check-out</p>
          <p className="font-medium text-gray-800">{formatDate(booking.checkOut)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide">Nights</p>
          <p className="font-medium text-gray-800">{nights}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide">Guests</p>
          <p className="font-medium text-gray-800">{booking.guestsCount}</p>
        </div>
      </div>

      {hasCurrencyMismatch && (
        <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
          <AlertCircle size={13} className="shrink-0 mt-0.5" aria-hidden="true" />
          Some charges use a different currency than this booking ({booking.currency}). Totals may be incorrect.
        </div>
      )}

      {/* Line items — grouped sections covering the whole folio */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <tbody>
            {/* Room & stay */}
            <MasterSectionRow label="Room & stay" />
            {!hasRoomChargeItem && (
              <LineRow label={`Accommodation (base rate) · ${nights} night${nights === 1 ? '' : 's'}`} amount={base} currency={booking.currency} subLabel={otaSub} />
            )}
            {roomItems.map(item => (
              <LineRow
                key={item.id}
                label={`${formatCategoryLabel(item.category)}${item.description ? ` – ${item.description}` : ''}`}
                amount={Number(item.amount) || 0}
                currency={booking.currency}
                date={item.date}
              />
            ))}

            {/* Food & beverage */}
            {foodItems.length > 0 && <MasterSectionRow label="Food & beverage" />}
            {foodItems.map(item => (
              <LineRow
                key={item.id}
                label={`${formatCategoryLabel(item.category)}${item.description ? ` – ${item.description}` : ''}`}
                amount={Number(item.amount) || 0}
                currency={booking.currency}
                date={item.date}
              />
            ))}

            {/* Taxes — auto per-section (room/food %) when configured, else the
                manually-posted tax lines, else the derived fallback. */}
            <MasterSectionRow label="Taxes" />
            {hasAutoTax ? (
              <>
                {taxBreakdown!.room.taxAmount > 0 && (
                  <LineRow label={`Room tax (${taxBreakdown!.room.taxRate}%)`} amount={taxBreakdown!.room.taxAmount} currency={booking.currency} muted />
                )}
                {taxBreakdown!.food.taxAmount > 0 && (
                  <LineRow label={`Food tax (${taxBreakdown!.food.taxRate}%)`} amount={taxBreakdown!.food.taxAmount} currency={booking.currency} muted />
                )}
              </>
            ) : taxItems.length > 0 ? (
              taxItems.map(item => (
                <LineRow key={item.id} label={item.description || 'Tax'} amount={Number(item.amount) || 0} currency={booking.currency} muted date={item.date} />
              ))
            ) : (
              <LineRow label="Taxes & Fees" amount={bookingTax} currency={booking.currency} muted />
            )}

            {/* Other */}
            {otherItems.length > 0 && <MasterSectionRow label="Other" />}
            {otherItems.map(item => (
              <LineRow
                key={item.id}
                label={`${formatCategoryLabel(item.category)}${item.description ? ` – ${item.description}` : ''}`}
                amount={Number(item.amount) || 0}
                currency={booking.currency}
                date={item.date}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Add tax / fee — the property sets its own tax as needed (no auto %). */}
      {!isCheckedOut && (
        <div className="border border-gray-200 rounded-xl p-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Add tax / fee</p>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-start">
            <div className="flex-1">
              <input
                type="text"
                value={taxForm.label}
                onChange={e => { setTaxErr(''); setTaxForm(f => ({ ...f, label: e.target.value })); }}
                placeholder="Label — e.g. GST 18%, City Tax, Service Charge"
                maxLength={120}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="w-full sm:w-36">
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={taxForm.amount}
                onChange={e => { setTaxErr(''); setTaxForm(f => ({ ...f, amount: e.target.value })); }}
                placeholder={`Amount (${booking.currency})`}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <button
              type="button"
              onClick={handleAddTax}
              disabled={addTax.isPending}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 shrink-0"
            >
              {addTax.isPending ? 'Adding…' : 'Add tax'}
            </button>
          </div>
          {taxErr && <p className="mt-2 text-xs text-red-600" role="alert">{taxErr}</p>}
          <p className="mt-2 text-xs text-gray-400">Adds a tax line to this bill at the amount you enter — set it however your property charges tax. It appears under Taxes and in the total.</p>
        </div>
      )}

      {/* Payment summary — backend-authoritative folio figures */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between gap-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Payment Summary</p>
          <PaymentTypeBadge paymentType={booking.paymentType} />
        </div>
        <div className="px-4 py-3 space-y-1.5 text-sm">
          {hasAutoTax && (
            <>
              <SummaryRow label="Room subtotal" value={taxBreakdown!.room.subtotal} currency={booking.currency} />
              {taxBreakdown!.room.taxAmount > 0 && (
                <SummaryRow label={`Room tax (${taxBreakdown!.room.taxRate}%)`} value={taxBreakdown!.room.taxAmount} currency={booking.currency} />
              )}
              {taxBreakdown!.food.subtotal > 0 && (
                <SummaryRow label="Food subtotal" value={taxBreakdown!.food.subtotal} currency={booking.currency} />
              )}
              {taxBreakdown!.food.taxAmount > 0 && (
                <SummaryRow label={`Food tax (${taxBreakdown!.food.taxRate}%)`} value={taxBreakdown!.food.taxAmount} currency={booking.currency} />
              )}
              <div className="border-t border-gray-100 my-1" />
            </>
          )}
          <SummaryRow label="Grand Total" value={folio.totalCharges} currency={booking.currency} bold />
          <SummaryRow label="Total Paid" value={folio.totalPayments} currency={booking.currency} />
          <div className="border-t border-gray-100 pt-2 mt-1">
            <SummaryRow
              label="Balance Due"
              value={folio.balance}
              currency={booking.currency}
              bold
              highlight={folio.balance > 0}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── RoomBillTab ──────────────────────────────────────────────────────────────

function RoomBillTab({ booking }: { booking: Booking }) {
  const { data: folio, isLoading } = useFolioSummary(booking.id);
  const { data: enriched } = useBooking(booking.id);
  const nights = calculateNights(booking.checkIn, booking.checkOut);
  const otaSub = otaNightlySubLine(enriched ?? booking, nights);

  const roomItems = folio?.items.filter(i => ROOM_CAT_SET.has(i.category)) ?? [];
  const taxItems = folio?.items.filter(i => i.category === 'tax') ?? [];
  const base = parseFloat(booking.baseAmount);
  const bookingTax = parseFloat(booking.totalAmount) - base;
  const roomAddOnTotal = roomItems.reduce((s, i) => s + parseFloat(i.amount), 0);
  // The room charge is captured either as a room_charge folio line or as the
  // booking's base rate — never both. Show the base line only when no folio
  // line already carries it, so the room subtotal isn't counted twice.
  const hasRoomChargeItem = roomItems.some(i => i.category === 'room_charge');

  // Prefer the backend's per-section figures (subtotal + configured room tax %),
  // so the Room Bill reconciles exactly with the Master Bill.
  const roomSection = folio?.tax?.room;
  const roomTax = roomSection ? roomSection.taxAmount : (taxItems.length > 0
    ? taxItems.reduce((s, i) => s + parseFloat(i.amount), 0)
    : bookingTax);
  const roomSubtotalPreTax = roomSection
    ? roomSection.subtotal
    : (hasRoomChargeItem ? roomAddOnTotal : base + roomAddOnTotal);
  const roomTotal = roomSection ? roomSection.total : roomSubtotalPreTax + roomTax;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={20} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stay summary */}
      <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide">Check-in</p>
          <p className="font-medium text-gray-800">{formatDate(booking.checkIn)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide">Check-out</p>
          <p className="font-medium text-gray-800">{formatDate(booking.checkOut)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide">Nights</p>
          <p className="font-medium text-gray-800">{nights}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide">Guests</p>
          <p className="font-medium text-gray-800">{booking.guestsCount}</p>
        </div>
      </div>

      {/* Room charges table */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Room & Stay Charges</p>
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {!hasRoomChargeItem && (
                <LineRow label="Accommodation (base rate)" amount={base} currency={booking.currency} subLabel={otaSub} />
              )}
              {roomItems.map(item => (
                <LineRow
                  key={item.id}
                  label={`${formatCategoryLabel(item.category)}${item.description ? ` – ${item.description}` : ''}`}
                  amount={parseFloat(item.amount)}
                  currency={booking.currency}
                />
              ))}
              {roomSection && roomSection.taxRate > 0
                ? roomSection.taxAmount > 0 && (
                  <LineRow label={`Room tax (${roomSection.taxRate}%)`} amount={roomSection.taxAmount} currency={booking.currency} muted />
                )
                : taxItems.length > 0
                  ? taxItems.map(item => (
                    <LineRow key={item.id} label={item.description || 'Tax'} amount={parseFloat(item.amount)} currency={booking.currency} muted />
                  ))
                  : <LineRow label="Taxes & Fees" amount={bookingTax} currency={booking.currency} muted />
              }
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment summary */}
      {folio && (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Payment Summary</p>
          </div>
          <div className="px-4 py-3 space-y-1.5 text-sm">
            <SummaryRow label="Room subtotal" value={roomSubtotalPreTax} currency={booking.currency} />
            {roomSection && roomSection.taxRate > 0 && (
              <SummaryRow label={`Room tax (${roomSection.taxRate}%)`} value={roomTax} currency={booking.currency} />
            )}
            <SummaryRow label="Room total" value={roomTotal} currency={booking.currency} bold />
            <SummaryRow label="Total paid" value={folio.totalPayments} currency={booking.currency} />
            <div className="border-t border-gray-100 pt-2 mt-1">
              <SummaryRow
                label="Balance due"
                value={roomTotal - folio.totalPayments}
                currency={booking.currency}
                bold
                highlight={roomTotal - folio.totalPayments > 0}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FoodBillTab ──────────────────────────────────────────────────────────────

function FoodBillTab({ booking }: { booking: Booking }) {
  const { data: folio, isLoading } = useFolioSummary(booking.id);
  const addItem = useAddFolioItem();
  const removeItem = useRemoveFolioItem();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Active Charge Types for this property (TASK-68, Flow 1). When configured,
  // selecting one prefills category/amount and links the line via chargeTypeId.
  // Absent / "Manual entry" ⇒ today's free-text behaviour, unchanged.
  const propertyId = useSelectedPropertyId();
  const { data: chargeTypesData } = useChargeTypes(
    { propertyId, isActive: true, limit: 100 },
    !!propertyId,
  );
  const chargeTypes = chargeTypesData?.data ?? [];

  const isCheckedOut = booking.status === 'checked_out';

  // Default date: min(today, booking checkout) — don't suggest future dates or dates after stay
  const defaultDate = (() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return today < booking.checkOut ? today : booking.checkOut;
  })();

  const [form, setForm] = useState({
    chargeTypeId: '' as string,
    category: 'breakfast' as string,
    description: '',
    amount: '',
    date: defaultDate,
  });
  const [formError, setFormError] = useState('');

  function handleSelectChargeType(id: string) {
    const ct = chargeTypes.find((c) => c.id === id);
    setFormError('');
    setForm((f) => ({
      ...f,
      chargeTypeId: id,
      // Prefill default amount when the charge type carries one; keep manual
      // amount otherwise.
      amount: ct && ct.defaultAmount != null ? parseFloat(ct.defaultAmount).toFixed(2) : f.amount,
    }));
  }

  const foodItems = folio?.items.filter(i => FOOD_CAT_SET.has(i.category)) ?? [];
  const foodSubtotal = foodItems.reduce((s, i) => s + parseFloat(i.amount), 0);
  // Backend food section carries the configured food tax %; total = subtotal + tax.
  const foodSection = folio?.tax?.food;
  const foodTax = foodSection ? foodSection.taxAmount : 0;
  const foodTotal = foodSection ? foodSection.total : foodSubtotal;
  const hasCurrencyMismatch = foodItems.some(i => i.currency && i.currency !== booking.currency);

  function handleAdd() {
    setFormError('');
    const amt = parseFloat(form.amount);
    if (!form.description.trim()) { setFormError('Description is required'); return; }
    if (isNaN(amt) || amt <= 0) { setFormError('Enter a valid amount > 0'); return; }

    addItem.mutate(
      {
        bookingId: booking.id,
        payload: {
          // When a charge type is selected, the backend prefills the category
          // from it; otherwise post the manually chosen food category.
          ...(form.chargeTypeId ? { chargeTypeId: form.chargeTypeId } : { category: form.category }),
          description: form.description.trim(),
          amount: amt,
          date: form.date,
        },
      },
      {
        onSuccess: () => setForm(f => ({ ...f, chargeTypeId: '', description: '', amount: '' })),
        onError: () => setFormError('Failed to add charge. Try again.'),
      },
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={20} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Late charge warning */}
      {isCheckedOut && (
        <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
          <AlertCircle size={13} className="shrink-0 mt-0.5" aria-hidden="true" />
          <span>This guest has checked out. Any charges added here are late charges and will need to be collected separately.</span>
        </div>
      )}

      {/* Add food charge form */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Add Food Charge</p>
        </div>
        <div className="p-4 space-y-3">
          {chargeTypes.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Charge Type</label>
              <select
                value={form.chargeTypeId}
                onChange={e => handleSelectChargeType(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Manual entry</option>
                {chargeTypes.map(ct => (
                  <option key={ct.id} value={ct.id}>
                    {ct.name}{ct.defaultAmount != null ? ` — ${parseFloat(ct.defaultAmount).toFixed(2)}` : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">Optional — prefills category, amount and taxable from the configured charge type.</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                disabled={!!form.chargeTypeId}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-400"
              >
                {FOOD_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
              <input
                type="date"
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <input
              type="text"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="e.g. Continental breakfast x2"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Amount ({booking.currency})
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={form.amount}
              onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              placeholder="0.00"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          {formError && (
            <div className="flex items-center gap-1.5 text-xs text-red-600">
              <AlertCircle size={12} aria-hidden="true" />
              {formError}
            </div>
          )}
          <button
            type="button"
            onClick={handleAdd}
            disabled={addItem.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            {addItem.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add Charge
          </button>
        </div>
      </div>

      {/* Food charges list */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Food & Beverage Charges
        </p>
        {hasCurrencyMismatch && (
          <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 mb-2">
            <AlertCircle size={13} className="shrink-0 mt-0.5" aria-hidden="true" />
            Some charges use a different currency than this booking ({booking.currency}). Totals may be incorrect.
          </div>
        )}
        {foodItems.length === 0 ? (
          <div className="border border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center py-8 text-center">
            <UtensilsCrossed size={28} className="text-gray-200 mb-2" aria-hidden="true" />
            <p className="text-sm text-gray-400">No food charges yet</p>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Item</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Date</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500">Amount</th>
                  <th className="px-4 py-2 w-8" />
                </tr>
              </thead>
              <tbody>
                {foodItems.map((item, idx) => (
                  <tr key={item.id} className={idx % 2 === 1 ? 'bg-gray-50/50' : ''}>
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-gray-800">{item.description}</p>
                      <p className="text-xs text-gray-400">{formatCategoryLabel(item.category)}</p>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">
                      {formatDate(item.date)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-gray-800 whitespace-nowrap">
                      {booking.currency} {parseFloat(item.amount).toFixed(2)}
                    </td>
                    <td className="px-2 py-2.5">
                      {confirmDeleteId === item.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => {
                              removeItem.mutate({ itemId: item.id, bookingId: booking.id });
                              setConfirmDeleteId(null);
                            }}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-red-600 text-white hover:bg-red-700"
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(null)}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(item.id)}
                          disabled={removeItem.isPending}
                          aria-label="Remove charge"
                          className="p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                        >
                          <Trash2 size={13} aria-hidden="true" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {foodSection && foodSection.taxRate > 0 && (
                  <>
                    <tr className="border-t border-gray-200">
                      <td colSpan={2} className="px-4 py-2 text-sm text-gray-500">Food subtotal</td>
                      <td className="px-4 py-2 text-right text-gray-700">{booking.currency} {foodSubtotal.toFixed(2)}</td>
                      <td />
                    </tr>
                    <tr>
                      <td colSpan={2} className="px-4 py-2 text-sm text-gray-500">Food tax ({foodSection.taxRate}%)</td>
                      <td className="px-4 py-2 text-right text-gray-700">{booking.currency} {foodTax.toFixed(2)}</td>
                      <td />
                    </tr>
                  </>
                )}
                <tr className="bg-gray-50 border-t border-gray-200">
                  <td colSpan={2} className="px-4 py-2.5 text-sm font-semibold text-gray-700">
                    Food Total
                  </td>
                  <td className="px-4 py-2.5 text-right font-bold text-gray-900">
                    {booking.currency} {foodTotal.toFixed(2)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── BillDetail ───────────────────────────────────────────────────────────────

function BillDetail({ booking }: { booking: Booking }) {
  const [activeTab, setActiveTab] = useState<'master' | 'room' | 'food'>('master');
  const { openBill, isPrinting, error, preview, lastInvoice, closePreview } = usePrintBill();
  // Enriched booking (channelPayload) for the source line — not fetched for the
  // list; opens the per-booking GET only when a bill is selected. Shared cache
  // with the Master/Room tabs (same query key).
  const { data: enriched } = useBooking(booking.id);
  const src = enriched ?? booking;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-200 shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {booking.guest?.fullName ?? 'Guest'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {booking.unit?.name ?? '—'} &middot; {calculateNights(booking.checkIn, booking.checkOut)} nights
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <PaymentTypeBadge paymentType={booking.paymentType} />
              <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                Source:
                <SourceBadge source={src.source} channelPayload={src.channelPayload} externalBookingId={src.externalBookingId} />
              </span>
            </div>
          </div>
          <span className={cn(
            'text-xs font-medium px-2 py-1 rounded-full shrink-0',
            STATUS_BADGE[booking.status] ?? 'bg-gray-100 text-gray-600',
          )}>
            {STATUS_LABEL[booking.status] ?? booking.status}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 shrink-0 px-5">
        <TabButton
          active={activeTab === 'master'}
          onClick={() => setActiveTab('master')}
          icon={<FileText size={14} aria-hidden="true" />}
          label="Master Bill"
        />
        <TabButton
          active={activeTab === 'room'}
          onClick={() => setActiveTab('room')}
          icon={<BedDouble size={14} aria-hidden="true" />}
          label="Room Bill"
        />
        <TabButton
          active={activeTab === 'food'}
          onClick={() => setActiveTab('food')}
          icon={<UtensilsCrossed size={14} aria-hidden="true" />}
          label="Food Bill"
        />
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-5">
        {activeTab === 'master' && <MasterBillTab booking={booking} />}
        {activeTab === 'room' && <RoomBillTab booking={booking} />}
        {activeTab === 'food' && <FoodBillTab booking={booking} />}
      </div>

      {/* Footer: generate bill */}
      <div className="shrink-0 px-5 py-4 border-t border-gray-200 bg-white">
        {error && (
          <p className="text-xs text-red-600 mb-2 flex items-center gap-1">
            <AlertCircle size={12} aria-hidden="true" /> {error}
          </p>
        )}
        {/* Bill No. banner — persists after modal closes so staff can always reference it */}
        {lastInvoice && !error && (
          <div className="flex items-center justify-between mb-3 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg">
            <span className="text-xs text-primary-700">Bill No. <span className="font-mono font-bold select-all">{lastInvoice.invoiceNumber}</span></span>
            <span className="text-[10px] text-primary-500">Use for tax claims</span>
          </div>
        )}
        <button
          type="button"
          onClick={() => {
            // The generated invoice is the complete folio (room + food + extras
            // + taxes), i.e. the master bill — surface the master view alongside it.
            setActiveTab('master');
            void openBill(booking.id);
          }}
          disabled={isPrinting}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          {isPrinting ? (
            <><Loader2 size={15} className="animate-spin" /> Generating…</>
          ) : lastInvoice ? (
            <><Receipt size={15} /> Re-open Master Bill</>
          ) : (
            <><Receipt size={15} /> Print Master Bill</>
          )}
        </button>
      </div>

      {preview && <BillPreviewModal preview={preview} onClose={closePreview} />}
    </div>
  );
}

// ─── Small helper components ──────────────────────────────────────────────────

function TabButton({ active, onClick, icon, label }: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors focus-visible:outline-none',
        active
          ? 'border-primary-600 text-primary-700'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function LineRow({ label, amount, currency, muted, date, subLabel }: {
  label: string;
  amount: number;
  currency: string;
  muted?: boolean;
  date?: string;
  /** Optional muted sub-line under the label (e.g. the OTA-charged nightly rate). */
  subLabel?: string | undefined;
}) {
  return (
    <tr className="border-b border-gray-100 last:border-0">
      <td className={cn('px-4 py-2.5 text-sm', muted ? 'text-gray-500' : 'text-gray-700')}>
        {label}
        {date && <span className="block text-xs text-gray-400 mt-0.5">{formatDate(date)}</span>}
        {subLabel && <span className="block text-xs text-indigo-500 mt-0.5">{subLabel}</span>}
      </td>
      <td className={cn('px-4 py-2.5 text-sm text-right whitespace-nowrap font-medium align-top', muted ? 'text-gray-500' : 'text-gray-800')}>
        {currency} {(Number(amount) || 0).toFixed(2)}
      </td>
    </tr>
  );
}

function MasterSectionRow({ label }: { label: string }) {
  return (
    <tr className="bg-gray-50 border-b border-gray-200">
      <td colSpan={2} className="px-4 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
        {label}
      </td>
    </tr>
  );
}

function SummaryRow({ label, value, currency, bold, highlight }: {
  label: string;
  value: number;
  currency: string;
  bold?: boolean;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className={cn('text-sm', bold ? 'font-semibold text-gray-900' : 'text-gray-600')}>{label}</span>
      <span className={cn(
        'text-sm font-medium',
        highlight ? 'text-red-600 font-bold' : bold ? 'text-gray-900 font-bold' : 'text-gray-800',
      )}>
        {currency} {(Number(value) || 0).toFixed(2)}
      </span>
    </div>
  );
}

// ─── BillsPage ────────────────────────────────────────────────────────────────

export function BillsPage() {
  const { selectedProperty } = usePropertyContext();
  const [statusFilter, setStatusFilter] = useState('active');
  const [monthDate, setMonthDate] = useState(new Date());
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [search, setSearch] = useState('');

  const monthStart = format(startOfMonth(monthDate), 'yyyy-MM-dd');
  const monthEnd = format(endOfMonth(monthDate), 'yyyy-MM-dd');

  // "active" is a UI-only pseudo-filter — send no status param and the backend
  // returns everything; we then client-filter out cancelled/no-show.
  // "checked_in" skips the date range because in-house guests may have checked
  // in during a previous month — filtering by check_in date would hide them.
  const apiStatus = statusFilter === 'active' || statusFilter === 'checked_in' ? undefined : statusFilter;
  const applyDateRange = statusFilter !== 'checked_in';

  const query: BookingsQuery = {
    ...(selectedProperty?.id ? { propertyId: selectedProperty.id } : {}),
    ...(apiStatus ? { status: apiStatus } : {}),
    ...(applyDateRange ? { from: monthStart, to: monthEnd } : {}),
    limit: 200,
  };

  const { data, isLoading } = useBookings(query);
  // API returns PaginatedResponse: { data: Booking[], meta: {...} }
  const rawBookings: Booking[] = Array.isArray(data) ? data : ((data as { data?: Booking[] })?.data ?? []);

  // Client-side filtering: exclude cancelled/no-show for billing; enforce checked_in when selected
  const EXCLUDED_STATUSES = new Set(['cancelled', 'no_show']);
  const bookings = rawBookings.filter(b => {
    if (EXCLUDED_STATUSES.has(b.status)) return false;
    if (statusFilter === 'checked_in' && b.status !== 'checked_in') return false;
    return true;
  });

  if (!selectedProperty) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Receipt size={40} className="text-gray-200 mb-3" aria-hidden="true" />
        <p className="text-sm font-semibold text-gray-700">No property selected</p>
        <p className="text-xs text-gray-400 mt-1">Select a property from the sidebar to view bills.</p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-48px)] bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm">
      {/* ── Left panel ─────────────────────────────────────────────────────── */}
      <div className="w-72 shrink-0 flex flex-col border-r border-gray-200 bg-white">
        {/* Header + filters */}
        <div className="px-4 pt-4 pb-3 border-b border-gray-100 space-y-3">
          <div className="flex items-center justify-between">
            <h1 className="text-sm font-bold text-gray-900">Bills</h1>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setMonthDate(d => subMonths(d, 1))}
                aria-label="Previous month"
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft size={14} aria-hidden="true" />
              </button>
              <span className="text-xs font-medium text-gray-700 min-w-[80px] text-center">
                {format(monthDate, 'MMM yyyy')}
              </span>
              <button
                type="button"
                onClick={() => setMonthDate(d => addMonths(d, 1))}
                aria-label="Next month"
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <ChevronRight size={14} aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Status filter pills */}
          <div className="flex flex-wrap gap-1">
            {STATUS_FILTERS.map(f => (
              <button
                key={f.value}
                type="button"
                onClick={() => setStatusFilter(f.value)}
                className={cn(
                  'text-xs px-2.5 py-1 rounded-full font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  statusFilter === f.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <BookingList
          bookings={bookings}
          selectedId={selectedBooking?.id ?? null}
          onSelect={setSelectedBooking}
          isLoading={isLoading}
          search={search}
          onSearch={setSearch}
        />
      </div>

      {/* ── Right panel ────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-hidden">
        {selectedBooking ? (
          <BillDetail key={selectedBooking.id} booking={selectedBooking} />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center px-8">
            <Receipt size={48} className="text-gray-200 mb-4" aria-hidden="true" />
            <p className="text-base font-semibold text-gray-800">Select a booking</p>
            <p className="text-sm text-gray-400 mt-1">
              Choose a booking from the list to view and manage its bill.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
